/**
 * Компонент колокольчика уведомлений
 * Отображается в хедере рядом с профилем пользователя
 */

import React, { useEffect, useRef, useState } from 'react'
import { useNotificationsStore, useUnreadCount } from '../store/notificationsStore'
import { useNotifications } from '../hooks/useNotifications'
import { useOrdersStore } from '../store/ordersStore'
import { 
  getNotificationIcon, 
  getPriorityColor, 
  formatNotificationTime,
  getOrderStatusText 
} from '../api/notificationsApi'
import { useNavigate } from 'react-router-dom'

interface NotificationBellProps {
  className?: string
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [showAnimation, setShowAnimation] = useState(false)
  
  // Уведомления с WebSocket поддержкой
  const {
    notifications,
    isLoading,
    error,
    loadNotifications,
    refreshStats,
    markAsRead,
    deleteNotification,
    deleteAllNotifications,
    clearError
  } = useNotifications()
  
  // UI состояние из store
  const { isDropdownOpen, setIsDropdownOpen } = useNotificationsStore()
  
  const unreadCount = useUnreadCount()
  
  // Получаем доступ к глобальному кэшу заказов
  const { allOrders } = useOrdersStore()
  
  // Функция для получения названия заказа из кэша
  const getOrderTitleFromCache = (orderId: string | number): string => {
    const order = allOrders.find(order => String(order.id) === String(orderId))
    if (order?.title) {
      return order.title // Реальное название: "Отвод 512 - 1 шт."
    }
    return `Заказ #${orderId}` // Fallback: "Заказ #1132"
  }
  
  // ============================================
  // ЭФФЕКТЫ
  // ============================================
  
  // Загрузка данных при монтировании (один раз)
  useEffect(() => {
    // Загружаем начальные уведомления и статистику
    loadNotifications({ limit: 20 })
    refreshStats()
  }, [])
  
  // Закрытие дропдауна при клике вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])
  
  // Анимация при новых уведомлениях
  useEffect(() => {
    if (unreadCount > 0) {
      setShowAnimation(true)
      const timer = setTimeout(() => setShowAnimation(false), 1000)
      return () => clearTimeout(timer)
    }
  }, [unreadCount])
  
  // Автообновление статистики каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      refreshStats()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [refreshStats])
  
  // ============================================
  // ОБРАБОТЧИКИ СОБЫТИЙ
  // ============================================
  
  const handleBellClick = async () => {
    // Сразу открываем дропдаун с лоадером
    if (!isDropdownOpen) {
      setIsDropdownOpen(true)
      // Загружаем свежие уведомления
      await loadNotifications({ limit: 20 })
    } else {
      // Просто закрываем если уже открыт
      setIsDropdownOpen(false)
    }
  }
  
  const handleNotificationClick = async (notificationId: string, orderId?: string) => {
    // Отмечаем как прочитанное
    const notification = notifications.find(n => n.id === notificationId)
    if (notification && !notification.is_read) {
      await markAsRead([notificationId])
    }
    
    // Переходим к заказу если есть order_id
    if (orderId) {
      navigate(`/orders/${orderId}`)
      setIsDropdownOpen(false)
    }
  }
  

  const handleClearAll = async () => {
    // Массовое удаление всех уведомлений одним запросом
    await deleteAllNotifications()
    
    // Закрываем дропдаун
    setIsDropdownOpen(false)
  }
  
  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation() // Предотвращаем клик по уведомлению
    await deleteNotification(notificationId)
  }
  
  const handleErrorDismiss = () => {
    clearError()
  }
  
  // ============================================
  // РЕНДЕР КОМПОНЕНТОВ
  // ============================================
  
  const renderNotificationItem = (notification: any) => {
    const timeText = formatNotificationTime(notification.created_at)
    
    // Определяем тип статуса из related_data или типа уведомления
    const status = notification.related_data?.new_status || notification.type
    
    // Получаем данные для отображения в зависимости от статуса
    const getNotificationDisplay = (status: string) => {
      switch (status) {
        case 'completed':
          return {
            title: 'Выполнен',
            icon: (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ),
            actionText: 'Выполнен',
            iconBgColor: 'bg-green-500'
          }
        case 'defect':
          return {
            title: 'Отмечен как брак',
            icon: (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ),
            actionText: 'Брак',
            iconBgColor: 'bg-yellow-500'
          }
        case 'pending_confirmation':
          return {
            title: 'Ждет подтверждения',
            icon: (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            actionText: 'Подтверждение',
            iconBgColor: 'bg-blue-500'
          }
        case 'rejected':
          return {
            title: 'Отклонен',
            icon: (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ),
            actionText: 'Отклонен',
            iconBgColor: 'bg-red-500'
          }
        case 'status_change':
        default:
          return {
            title: notification.title || 'Изменение статуса',
            icon: (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            actionText: getOrderStatusText(status),
            iconBgColor: 'bg-gray-500'
          }
      }
    }
    
    const display = getNotificationDisplay(status)
    
    return (
      <div
        key={notification.id}
        className={`
          flex items-start p-2 sm:p-3 bg-white rounded-lg shadow-sm border border-gray-200 relative cursor-pointer
          hover:shadow-md transition-all duration-200
          ${!notification.is_read ? 'ring-2 ring-blue-100 bg-blue-50' : ''}
        `}
        onClick={() => handleNotificationClick(notification.id, notification.order_id)}
      >
        {/* Иконка статуса */}
        <div className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full shrink-0 ${display.iconBgColor}`}>
          <div className="text-xs sm:text-sm">
            {display.icon}
          </div>
        </div>
        
        {/* Содержимое */}
        <div className="ml-2 sm:ml-3 flex-1">
          <div className="flex justify-between items-start">
            <h4 className={`text-sm sm:text-base font-medium text-gray-900 ${!notification.is_read ? 'font-semibold' : ''}`}>
              {display.title}
            </h4>
            
            {/* Кнопка удаления */}
            <button
              onClick={(e) => handleDeleteNotification(notification.id, e)}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-2 sm:ml-4"
              title="Удалить уведомление"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Информация о заказе */}
          {notification.order_id && (
            <div className="mt-1 sm:mt-2 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
              <span 
                className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  handleNotificationClick(notification.id, notification.order_id)
                }}
              >
                {getOrderTitleFromCache(notification.order_id)}
              </span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-600">{display.actionText}</span>
            </div>
          )}
          
          {/* Время */}
          <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">{timeText}</p>
          
          {/* Индикатор непрочитанного */}
          {!notification.is_read && (
            <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full"></div>
          )}
        </div>
      </div>
    )
  }
  
  // ============================================
  // ОСНОВНОЙ РЕНДЕР
  // ============================================
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Кнопка колокольчика */}
      <button
        onClick={handleBellClick}
        className={`
          relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full 
          transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500
          ${showAnimation ? 'animate-bounce' : ''}
        `}
        title="Уведомления"
      >
        {/* Иконка колокольчика */}
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
          />
        </svg>
        
        {/* Счетчик непрочитанных */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Выпадающий список уведомлений */}
      {isDropdownOpen && (
        <div className="fixed top-4 right-4 sm:absolute sm:right-0 sm:top-auto mt-0 sm:mt-2 w-80 sm:w-96 bg-gray-100 rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Заголовок */}
          <div className="bg-white rounded-t-xl p-3 sm:p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Уведомления</h2>
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Очистить все
                </button>
              )}
            </div>
          </div>
          
          {/* Ошибка */}
          {error && (
            <div className="p-3 bg-red-50 border-b border-red-200 mx-4 mt-2 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700">{error}</span>
                <button
                  onClick={handleErrorDismiss}
                  className="text-red-500 hover:text-red-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          {/* Список уведомлений */}
          <div className="max-h-80 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">Загрузка...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500 bg-white rounded-lg">
                <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="text-sm">Нет уведомлений</span>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.slice(0, 10).map(renderNotificationItem)}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  )
}
